import { useCallback, useMemo, useSyncExternalStore } from "react";
import {
  getProjectStartGuideStatus,
  getServerStartGuideStatus,
  getStartGuideStatusSnapshot,
  recordClientStep,
  setStartGuideCollapsed,
  setStartGuideDismissed,
  setStartGuideNotice,
  subscribeStartGuideStatus,
} from "./startGuideClientStatus";
import { isServerStartGuideStatus } from "./startGuideStatusSource";
import { resolveStartGuideSteps } from "./resolveStartGuideSteps";
import { getStartGuideContent } from "../content";

function buildStepsContent(content, project, onRecord) {
  const stepsContent = {};

  Object.entries(content.steps).forEach(([id, step]) => {
    const definition = content.defaultSteps.find((item) => item.id === id);
    const go = ({ navigate }) => {
      if (step.to) navigate(step.to);
    };
    const actions = [
      {
        id: `${id}-go`,
        label: step.actionLabel,
        variant: "primary",
        onClick: go,
      },
    ];

    (step.secondaryActions || []).forEach((action, index) => {
      actions.push({
        id: `${id}-secondary-${index}`,
        label: action.label,
        to: action.to,
        href: action.href,
      });
    });

    if (step.learnMore) {
      actions.push({
        id: `${id}-learn`,
        label: step.learnMore.label || "Learn more",
        to: step.learnMore.to,
        href: step.learnMore.href,
      });
    }

    if (definition?.optional) {
      actions.push({
        id: `${id}-skip`,
        label: "Skip",
        onClick: () => onRecord(id, true),
      });
    } else if (!isServerStartGuideStatus() && definition?.category !== "learn") {
      actions.push({
        id: `${id}-saved`,
        label: "I've saved this",
        onClick: () => onRecord(id, true),
      });
    }

    stepsContent[id] = {
      name: step.name,
      description: step.description,
      actions,
    };
  });

  return stepsContent;
}

export function useProjectStartGuide(project, content, signals = {}) {
  const snapshot = useSyncExternalStore(
    subscribeStartGuideStatus,
    getStartGuideStatusSnapshot,
    getStartGuideStatusSnapshot
  );
  const projectStatus = snapshot.projects[project] || getProjectStartGuideStatus(project);
  const serverStatus = snapshot.server[project] || getServerStartGuideStatus(project);

  const record = useCallback(
    (stepId, complete = true) => {
      recordClientStep(project, stepId, complete);
      if (!complete) return;
      const currentIndex = content.defaultSteps.findIndex((step) => step.id === stepId);
      const next = content.defaultSteps
        .slice(currentIndex + 1)
        .find((step) => (step.category || "required") === "required");
      const current = content.defaultSteps[currentIndex];
      setStartGuideNotice(project, {
        stepId,
        message: next
          ? `${current?.name || "Step"} saved. Next task: ${next.name}.`
          : `${current?.name || "Step"} saved.`,
      });
    },
    [content.defaultSteps, project]
  );

  const steps = useMemo(
    () =>
      resolveStartGuideSteps({
        steps: content.defaultSteps,
        clientSteps: projectStatus.steps,
        serverStatus,
        signals,
      }),
    [content.defaultSteps, projectStatus.steps, serverStatus, signals]
  );

  const stepsContent = useMemo(
    () => buildStepsContent(content, project, record),
    [content, project, record]
  );

  return {
    steps,
    stepsContent,
    dismissed: Boolean(projectStatus.dismissed),
    collapsed: Boolean(projectStatus.collapsed),
    notice: projectStatus.notice,
    dismiss: () => setStartGuideDismissed(project, true),
    restore: () => setStartGuideDismissed(project, false),
    setCollapsed: (collapsed) => setStartGuideCollapsed(project, collapsed),
    clearNotice: () => setStartGuideNotice(project, null),
    usesClientStatus: !isServerStartGuideStatus(),
  };
}

export function notifyStartGuideSaved(project, stepId) {
  if (!project || !stepId) return;
  recordClientStep(project, stepId, true);
  const steps = getStartGuideContent(project)?.defaultSteps || [];
  const index = steps.findIndex((step) => step.id === stepId);
  const current = steps[index];
  const next = steps
    .slice(index + 1)
    .find((step) => (step.category || "required") === "required");
  setStartGuideNotice(project, {
    stepId,
    message: next
      ? `${current?.name || "Step"} saved. Next task: ${next.name}.`
      : `${current?.name || "Step"} saved.`,
  });
}
